import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getTenantIdFromURL from './utils/getTenantIdFromURL';
import {
  getCompanyUploadedFileSignedUrl,
  listCompanyUploadedFiles,
  getCompanyUploadedFileDownloadUrl,
  deleteCompanyUploadedFile
} from './api/companyApi';

const ACCEPTED_EXTENSIONS = ['.csv', '.xls', '.xlsx'];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(',');

const isAllowedFile = (name) => {
  const lower = name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

export default function UploadCompanyFiles() {
  const URLTenant = getTenantIdFromURL();
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenantId = useSelector((state) => state.userInfo.tenantId);
  const tenantId = isAdmin ? URLTenant : currentTenantId;

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadName, setCurrentUploadName] = useState('');
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const fileInputRef = useRef(null);

  const fetchFiles = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const response = await listCompanyUploadedFiles(tenantId);
      setFiles(response?.data?.files || []);
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
      toast.error('Failed to fetch uploaded files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [tenantId]);

  const uploadSingleFile = async (file) => {
    const signedUrlResponse = await getCompanyUploadedFileSignedUrl(tenantId, {
      fileName: file.name,
      fileType: file.type
    });

    if (!signedUrlResponse?.data?.uploadUrl) {
      throw new Error(signedUrlResponse?.message || 'Failed to get upload URL');
    }

    await axios.put(signedUrlResponse.data.uploadUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream'
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        }
      }
    });
  };

  const handleFilesSelected = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (selected.length === 0) return;

    const invalid = selected.filter((f) => !isAllowedFile(f.name));
    if (invalid.length > 0) {
      toast.error(
        `Unsupported file type${invalid.length > 1 ? 's' : ''}: ${invalid
          .map((f) => f.name)
          .join(', ')}. Allowed: .csv, .xls, .xlsx`
      );
      return;
    }

    if (!tenantId) {
      toast.error('Missing tenant context');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failureCount = 0;

    for (const file of selected) {
      setCurrentUploadName(file.name);
      setUploadProgress(0);
      try {
        await uploadSingleFile(file);
        successCount += 1;
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
        failureCount += 1;
      }
    }

    setUploading(false);
    setCurrentUploadName('');
    setUploadProgress(0);

    if (successCount > 0) {
      toast.success(
        `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully`
      );
    }
    if (failureCount > 0) {
      toast.error(
        `${failureCount} file${failureCount > 1 ? 's' : ''} failed to upload`
      );
    }

    fetchFiles();
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await getCompanyUploadedFileDownloadUrl(
        tenantId,
        file.fileGCSKey
      );
      const downloadUrl = response?.data?.downloadUrl;
      if (!downloadUrl) {
        throw new Error('No download URL returned');
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async () => {
    if (!fileToDelete || !tenantId) return;

    const file = fileToDelete;
    setDeletingKey(file.fileGCSKey);
    try {
      await deleteCompanyUploadedFile(tenantId, file.fileGCSKey);
      setFiles((current) => current.filter(
        (item) => item.fileGCSKey !== file.fileGCSKey
      ));
      setFileToDelete(null);
      toast.success(`${file.fileName} deleted`);
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ maxWidth: 900, mx: 'auto' }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Company Files
            </Typography>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={handleUploadClick}
              disabled={uploading || !tenantId}
            >
              Upload File
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              style={{ display: 'none' }}
              onChange={handleFilesSelected}
            />
          </Stack>

          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            Supported file types: .csv, .xls, .xlsx
          </Typography>

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Uploading {currentUploadName} ({uploadProgress}%)
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : files.length === 0 ? (
            <Typography
              variant="body1"
              sx={{ color: '#6B7280', textAlign: 'center', py: 4 }}
            >
              No files uploaded
            </Typography>
          ) : (
            <List dense>
              {files.map((file) => (
                <ListItem
                  key={file.fileGCSKey}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        aria-label={`Download ${file.fileName}`}
                        onClick={() => handleDownload(file)}
                        disabled={deletingKey === file.fileGCSKey}
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label={`Delete ${file.fileName}`}
                        onClick={() => setFileToDelete(file)}
                        disabled={deletingKey === file.fileGCSKey}
                        sx={{ color: '#DC2626' }}
                      >
                        {deletingKey === file.fileGCSKey
                          ? <CircularProgress size={20} color="inherit" />
                          : <DeleteOutlineIcon />}
                      </IconButton>
                    </Stack>
                  }
                  sx={{
                    borderBottom: '1px solid #E5E7EB',
                    '&:last-child': { borderBottom: 'none' }
                  }}
                >
                  <ListItemText primary={file.fileName} />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={Boolean(fileToDelete)}
        onClose={() => {
          if (!deletingKey) setFileToDelete(null);
        }}
        aria-labelledby="delete-company-file-title"
      >
        <DialogTitle id="delete-company-file-title">Delete file?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {fileToDelete
              ? `Delete “${fileToDelete.fileName}”? This cannot be undone.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFileToDelete(null)}
            disabled={Boolean(deletingKey)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={Boolean(deletingKey)}
          >
            {deletingKey ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
